import { Shield, ArrowRight, ExternalLink } from 'lucide-react';
import { isClosingSoon } from '../../services/samApi';
import { PerformanceRadar } from './PerformanceRadar';

interface WarRoomHeaderProps {
    contract: any;
    savedContracts: any[];
    onContractSelect: (contract: any) => void;
    daysRemaining: string;
    agencyName: string;
}

export function WarRoomHeader({
    contract,
    savedContracts,
    onContractSelect,
    daysRemaining,
    agencyName
}: WarRoomHeaderProps) {
    return (
        <header className="sticky top-0 z-30 bg-gradient-to-b from-[#050505] via-[#050505]/95 to-transparent backdrop-blur-md pb-8 pt-6 px-8 lg:px-12 border-b border-white/5">
            <div className="max-w-[1600px] mx-auto">

                {/* TOP ROW: TARGET SELECTOR */}
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-900/20 border border-blue-500/20 rounded-full">
                            <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                            <span className="text-xs font-bold text-blue-400 uppercase tracking-widest">Live Operations</span>
                        </div>

                        <div className="relative group">
                            <select
                                className="appearance-none bg-[#0A0A0A] hover:bg-[#111] border border-white/10 text-white pl-4 pr-10 py-2 rounded-lg text-sm font-medium outline-none focus:ring-1 focus:ring-blue-500 transition-all cursor-pointer min-w-[300px]"
                                value={contract?.noticeId || ''}
                                onChange={(e) => {
                                    const selected = savedContracts.find(c => c.noticeId === e.target.value);
                                    if (selected) {
                                        onContractSelect(selected);
                                    }
                                }}
                            >
                                {savedContracts.map(c => (
                                    <option key={c.noticeId} value={c.noticeId} className="bg-[#050505] text-gray-300">
                                        {c.title.substring(0, 50)}{c.title.length > 50 ? '...' : ''}
                                    </option>
                                ))}
                            </select>
                            {/* Chevron Icon */}
                            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                                <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex items-start justify-between">
                    <div className="space-y-4 max-w-3xl">
                        <div className="flex items-center gap-3 animate-fade-in-up">
                            <Shield className="w-5 h-5 text-gray-600" />
                            <span className="text-sm font-bold text-gray-500 tracking-widest uppercase">{agencyName}</span>
                            <span className="w-1.5 h-1.5 bg-gray-800 rounded-full" />
                            <span className="text-sm font-mono text-gray-600">{contract?.solicitationNumber}</span>
                        </div>
                        <h1 className="text-3xl md:text-5xl font-bold text-white tracking-tight leading-tight drop-shadow-2xl">
                            {contract?.title}
                        </h1>
                    </div>

                    <div className="hidden lg:flex flex-col items-end gap-6 animate-fade-in-left">
                        <button className="group relative px-8 py-3 bg-blue-600 hover:bg-blue-500 text-white font-bold tracking-wide rounded-full shadow-[0_0_20px_rgba(37,99,235,0.4)] hover:shadow-[0_0_30px_rgba(37,99,235,0.6)] transition-all flex items-center gap-3 overflow-hidden">
                            <span className="relative z-10">INITIATE BID</span>
                            <ArrowRight className="w-5 h-5 relative z-10 group-hover:translate-x-1 transition-transform" />
                            <div className="absolute inset-0 bg-gradient-to-r from-blue-600 via-blue-400 to-blue-600 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-[length:200%_auto] animate-gradient" />
                        </button>

                        <div className="flex items-center gap-8 mt-2">
                            {contract?.fitComponents && (
                                <div className="p-4 bg-white/5 rounded-2xl border border-white/10 scale-90 origin-right">
                                    <h4 className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-2 text-center">Fit Score Breakdown</h4>
                                    <PerformanceRadar scores={contract.fitComponents} />
                                </div>
                            )}

                            <div className="flex flex-col items-end gap-4 min-w-[120px]">
                                <span className="text-gray-500 text-[10px] font-bold uppercase tracking-widest">Overall Match</span>
                                <div className="flex items-center gap-3">
                                    <div className="relative w-4 h-4">
                                        <div className="absolute inset-0 bg-green-500 rounded-full animate-ping opacity-50" />
                                        <div className="relative w-4 h-4 bg-green-500 rounded-full" />
                                    </div>
                                    <span className="text-4xl font-bold text-white leading-none">{contract?.fitScore || 0}%</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* CINEMATIC METRICS ROW */}
                <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-12 border-b border-white/5 pb-8">
                    <div>
                        <p className="text-[10px] font-bold text-gray-600 uppercase tracking-widest mb-2">Value Estimate</p>
                        <p className="text-2xl font-medium text-white tracking-tight">$2.5M <span className="text-gray-600 text-lg font-normal">-</span> $5M</p>
                    </div>
                    <div>
                        <p className="text-[10px] font-bold text-gray-600 uppercase tracking-widest mb-2">Deadline</p>
                        <p className={`text-2xl font-medium tracking-tight ${isClosingSoon(contract?.responseDeadline) ? 'text-amber-500' : 'text-white'}`}>
                            {daysRemaining} <span className="text-lg font-normal text-gray-500">Days</span>
                        </p>
                    </div>
                    <div>
                        <p className="text-[10px] font-bold text-gray-600 uppercase tracking-widest mb-2">Set-Aside</p>
                        <p className="text-2xl font-medium text-white tracking-tight truncate">{contract?.typeOfSetAside || 'None'}</p>
                    </div>
                    <div className="flex flex-col justify-end">
                        <div className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors cursor-pointer group">
                            <ExternalLink className="w-4 h-4 group-hover:stroke-blue-400" />
                            {contract?.uiLink && (
                                <a href={contract.uiLink} target="_blank" rel="noopener noreferrer" className="text-sm font-medium border-b border-transparent group-hover:border-blue-400">View Source</a>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </header>
    );
}
